'use client';

import React from 'react';

interface ReasoningFormatterProps {
  text: string;
  className?: string;
}

export default function ReasoningFormatter({ text, className = '' }: ReasoningFormatterProps) {
  const formatText = (text: string) => {
    // Split text into lines for processing
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Skip empty lines but preserve spacing
      if (!line.trim()) {
        return <div key={lineIndex} className="h-2" />;
      }

      // Process bold text (**text** -> <strong>text</strong>)
      const processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Determine line type and apply appropriate styling
      let lineClass = 'leading-relaxed';
      
      // Section headers (lines ending with evaluation, review, status, rule)
      if (line.includes('evaluation') || line.includes('review:') || line.includes('status:') || line.includes('rule:')) {
        lineClass += ' text-base font-semibold text-zinc-800 dark:text-zinc-200 mt-4 mb-2';
      }
      // Bullet points
      else if (line.trim().startsWith('•')) {
        lineClass += ' ml-4 text-zinc-700 dark:text-zinc-300';
      }
      // Emoji headers (lines starting with emoji)
      else if (/^[🧭🧮📊🔎🎯ℹ️]/.test(line.trim())) {
        lineClass += ' text-zinc-800 dark:text-zinc-200 font-medium mt-3 mb-1';
      }
      // Status lines (✅, ❌)
      else if (line.includes('✅') || line.includes('❌')) {
        if (line.includes('Fail') || line.includes('WILL EXIT')) {
          lineClass += ' text-red-700 dark:text-red-300 font-medium';
        } else if (line.includes('Pass') || line.includes('WILL STAY')) {
          lineClass += ' text-green-700 dark:text-green-300 font-medium';
        } else {
          lineClass += ' text-zinc-700 dark:text-zinc-300';
        }
      }
      // Regular text
      else {
        lineClass += ' text-zinc-700 dark:text-zinc-300';
      }

      return (
        <div
          key={lineIndex}
          className={lineClass}
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    });
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {formatText(text)}
    </div>
  );
}
